import React, { useEffect, useMemo, useState } from "react";
import { collection, db, deleteDoc, doc, setDoc } from "../services/dbMariaFirestoreCompat";
import EditModal from "./EditModal";
import { useAuth } from "../contexts/AuthContext";
import { getCachedCollection, setCachedCollection, upsertCachedRecord, removeCachedRecord } from "../services/firestoreCache";
import { getApiBaseUrl } from "../services/dbMariaAuth";

interface RecordType {
  id?: string;
  _skipReason?: string;
  [key: string]: any;
}

type ImportStatus = "ready" | "duplicateInFile" | "duplicateInCollection" | "invalid";

interface ImportPreviewRow {
  docId: string;
  record: RecordType;
  status: ImportStatus;
  reason?: string;
}

interface DataTableProps {
  collectionName: string;
  onStatsChange?: (stats: { total: number; producers: number; countries: number }) => void;
}

// Hook do opóźnienia (debounce) wartości
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Poprawiona funkcja do formatowania wartości Firestore do filtrowania
const formatValue = (value: any): string => {
  // Dla wartości null lub undefined
  if (value === null || value === undefined) {
    return '';
  }

  // Sprawdź czy wartość jest obiektem i zawiera klucze seconds i nanoseconds (Firestore Timestamp)
  if (value && typeof value === 'object') {
    if ('seconds' in value && 'nanoseconds' in value) {
      // Konwersja Timestamp na format daty
      const date = new Date(value.seconds * 1000);
      return date.toLocaleString();
    }
    // Dla innych typów obiektów
    return JSON.stringify(value);
  }

  // Pozostałe typy konwertujemy bezpośrednio do stringa
  return String(value);
};

// Pomocniczo: pobierz wartość pola bez względu na wielkość liter
const getFieldValue = (record: RecordType, fieldName: string) => {
  const key = Object.keys(record).find(k => k.toLowerCase() === fieldName.toLowerCase());
  return key ? record[key] : undefined;
};

const getTimestampValueMs = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;

  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }

  if (typeof value === "object") {
    if ("seconds" in value && typeof (value as any).seconds === "number") {
      return (value as any).seconds * 1000;
    }
    if ("toDate" in value && typeof (value as any).toDate === "function") {
      const date = (value as any).toDate();
      return Number.isNaN(date?.getTime?.()) ? 0 : date.getTime();
    }

    const dateLikeKeys = ["date", "value", "timestamp", "$date", "created_at", "createdat", "created_on", "createdon", "datetime", "time"];
    for (const key of dateLikeKeys) {
      const nestedValue = (value as Record<string, any>)[key];
      if (nestedValue !== undefined) {
        const nestedMs = getTimestampValueMs(nestedValue);
        if (nestedMs > 0) return nestedMs;
      }
    }

    for (const [key, nestedValue] of Object.entries(value as Record<string, any>)) {
      const lowerKey = key.toLowerCase();
      if (/(created|added|date|timestamp|time)/.test(lowerKey)) {
        const nestedMs = getTimestampValueMs(nestedValue);
        if (nestedMs > 0) return nestedMs;
      }
    }
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) return 0;

    if (/^\d{10}(\.\d+)?$/.test(normalized)) {
      const numeric = Number(normalized);
      return Number.isFinite(numeric) ? (numeric > 1e12 ? numeric : numeric * 1000) : 0;
    }

    if (/^\d{13,}$/.test(normalized)) {
      const numeric = Number(normalized);
      return Number.isFinite(numeric) ? numeric : 0;
    }

    const candidateStrings = [
      normalized,
      normalized.replace(/\s+/, "T"),
      normalized.replace(" ", "T"),
      normalized.replace(/\s+/g, "T"),
      normalized.replace(/\.?\d{3}Z$/i, "Z"),
      normalized.replace(/\s+UTC$/i, "Z")
    ];

    for (const candidate of candidateStrings) {
      const parsed = Date.parse(candidate);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return 0;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return 0;
    return value > 1e12 ? value : value * 1000;
  }

  return 0;
};

const getAddedAtTimestamp = (record: RecordType) => {
  const candidates = [
    "addedAt",
    "createdAt",
    "created_at",
    "created",
    "createdOn",
    "created_on",
    "timestamp",
    "date",
    "updatedAt",
    "updated_at"
  ];

  for (const fieldName of candidates) {
    const val = getFieldValue(record, fieldName);
    const ts = getTimestampValueMs(val);
    if (ts > 0) return ts;
  }

  const dateLikeKeys = Object.keys(record).filter(key => /(created|added|date|timestamp|time)/i.test(key));
  for (const key of dateLikeKeys) {
    const ts = getTimestampValueMs(record[key]);
    if (ts > 0) return ts;
  }

  return 0;
};

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).replace(",", ".").match(/-?\d+(\.\d+)?/);
  if (!normalized) return null;
  const parsed = Number(normalized[0]);
  return Number.isFinite(parsed) ? parsed : null;
};

const mapTextSizeToNumber = (value: string): number | null => {
  const normalized = value.trim().toUpperCase();
  const textMap: Record<string, number> = {
    "XXS": 44,
    "XS": 47,
    "S": 50,
    "M": 54,
    "L": 58,
    "XL": 61,
    "XXL": 64
  };
  return textMap[normalized] ?? null;
};

const getSizeSortValue = (sizeValue: unknown): number => {
  const numeric = toNumber(sizeValue);
  if (numeric !== null) return numeric;
  const text = String(sizeValue ?? "");
  const mapped = mapTextSizeToNumber(text);
  return mapped ?? Number.MAX_SAFE_INTEGER;
};

const getStatusLabel = (status: ImportStatus): string => {
  if (status === "ready") return "Gotowy do zapisu";
  if (status === "duplicateInFile") return "Duplikat w pliku";
  if (status === "duplicateInCollection") return "Duplikat w kolekcji";
  return "Błędny rekord";
};

const countryNameFormatter = typeof Intl !== "undefined" && "DisplayNames" in Intl
  ? new Intl.DisplayNames(["pl", "en"], { type: "region" })
  : null;

const getCountryDisplayName = (country: string | null) => {
  if (!country || country === "ALL") return "";
  const normalized = country.trim();
  if (!normalized) return "";

  if (/^[A-Za-z]{2}$/.test(normalized) && countryNameFormatter) {
    const displayName = countryNameFormatter.of(normalized.toUpperCase());
    if (displayName) return displayName.toUpperCase();
  }

  return normalized.toUpperCase();
};

const FrameSizePreview: React.FC<{ record: RecordType; width?: number; height?: number }> = ({
  record,
  width = 140,
  height = 75
}) => {
  const wheelSizeInches = toNumber(getFieldValue(record, "wheelSizeInches")) ?? 28;
  const wheelRadiusMm = Math.max(200, (wheelSizeInches * 25.4) / 2);
  const chainstayMm = toNumber(getFieldValue(record, "chainstayMm")) ?? 410;
  const bbDropMm = toNumber(getFieldValue(record, "bbDrop")) ?? 70;
  const reachMm = toNumber(getFieldValue(record, "reach")) ?? 380;
  const stackMm = toNumber(getFieldValue(record, "stack")) ?? 560;
  const seatTubeLengthMm = toNumber(getFieldValue(record, "seatTubeLengthMm")) ?? 500;
  const headTubeLengthMm = toNumber(getFieldValue(record, "headTubeLengthMm")) ?? 140;
  const seatAngleDeg = toNumber(getFieldValue(record, "seatAngleDegrees")) ?? 73;
  const headAngleDeg = toNumber(getFieldValue(record, "headAngleDegrees")) ?? 73;
  const rakeMm = toNumber(getFieldValue(record, "rake")) ?? 45;

  const seatAngleRad = (seatAngleDeg * Math.PI) / 180;
  const headAngleRad = (headAngleDeg * Math.PI) / 180;

  const rearAxle = { x: 0, y: 0 };
  const bbX = Math.sqrt(Math.max(0, chainstayMm * chainstayMm - bbDropMm * bbDropMm));
  const bb = { x: bbX, y: -bbDropMm };
  const seatTop = {
    x: bb.x - Math.cos(seatAngleRad) * seatTubeLengthMm,
    y: bb.y + Math.sin(seatAngleRad) * seatTubeLengthMm
  };
  const headTop = {
    x: bb.x + reachMm,
    y: bb.y + stackMm
  };
  const headBottom = {
    x: headTop.x + Math.cos(headAngleRad) * headTubeLengthMm,
    y: headTop.y - Math.sin(headAngleRad) * headTubeLengthMm
  };

  const storedWheelBase = toNumber(getFieldValue(record, "wheelBase"));
  const computedWheelBase = bbX + reachMm + (stackMm - bbDropMm) * Math.tan((Math.PI / 2) - headAngleRad) + rakeMm * Math.cos((Math.PI / 2) - headAngleRad);
  const wheelBaseMm = Math.max(bbX + 250, storedWheelBase ?? computedWheelBase);
  const frontAxle = { x: wheelBaseMm, y: 0 };

  const points = [rearAxle, frontAxle, bb, seatTop, headTop, headBottom];
  const minX = Math.min(...points.map(p => p.x), rearAxle.x - wheelRadiusMm, frontAxle.x - wheelRadiusMm);
  const maxX = Math.max(...points.map(p => p.x), rearAxle.x + wheelRadiusMm, frontAxle.x + wheelRadiusMm);
  const minY = Math.min(...points.map(p => p.y), -wheelRadiusMm);
  const maxY = Math.max(...points.map(p => p.y), wheelRadiusMm);

  const svgWidth = width;
  const svgHeight = height;
  const margin = 6;
  const contentWidth = svgWidth - margin * 2;
  const contentHeight = svgHeight - margin * 2;
  const widthMm = Math.max(1, maxX - minX);
  const heightMm = Math.max(1, maxY - minY);
  const scale = Math.min(contentWidth / widthMm, contentHeight / heightMm);

  const px = (point: { x: number; y: number }) => ({
    x: margin + (point.x - minX) * scale,
    y: margin + (maxY - point.y) * scale
  });

  const rearAxlePx = px(rearAxle);
  const frontAxlePx = px(frontAxle);
  const bbPx = px(bb);
  const seatTopPx = px(seatTop);
  const headTopPx = px(headTop);
  const headBottomPx = px(headBottom);
  const wheelRadiusPx = Math.max(5, wheelRadiusMm * scale);

  return (
    <svg
      width={svgWidth}
      height={svgHeight}
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      aria-label={`Rama ${String(getFieldValue(record, "rozmiar") ?? "")}`}
    >
      <circle cx={rearAxlePx.x} cy={rearAxlePx.y} r={wheelRadiusPx} fill="none" stroke="#3a506b" strokeWidth="1.6" />
      <circle cx={frontAxlePx.x} cy={frontAxlePx.y} r={wheelRadiusPx} fill="none" stroke="#3a506b" strokeWidth="1.6" />
      <line x1={rearAxlePx.x} y1={rearAxlePx.y} x2={bbPx.x} y2={bbPx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={bbPx.x} y1={bbPx.y} x2={seatTopPx.x} y2={seatTopPx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={seatTopPx.x} y1={seatTopPx.y} x2={headTopPx.x} y2={headTopPx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={headTopPx.x} y1={headTopPx.y} x2={headBottomPx.x} y2={headBottomPx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={headBottomPx.x} y1={headBottomPx.y} x2={bbPx.x} y2={bbPx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={headBottomPx.x} y1={headBottomPx.y} x2={frontAxlePx.x} y2={frontAxlePx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={seatTopPx.x} y1={seatTopPx.y} x2={rearAxlePx.x} y2={rearAxlePx.y} stroke="#64ffda" strokeWidth="1.8" />
    </svg>
  );
};

const DataTable: React.FC<DataTableProps> = ({ collectionName, onStatsChange }) => {
  const [records, setRecords] = useState<RecordType[]>([]);
  const [filter, setFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<{[key: string]: string}>({});
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [columns, setColumns] = useState<string[]>([]);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dropTargetColumn, setDropTargetColumn] = useState<string | null>(null);
  const [editingRecord, setEditingRecord] = useState<RecordType | null>(null);
  const [, setActionMenuOpen] = useState<string | null>(null);
  const [skippedRecords, setSkippedRecords] = useState<RecordType[]>([]);
  const [showSkippedModal, setShowSkippedModal] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importProgress, setImportProgress] = useState<number>(0);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [showImportPreviewModal, setShowImportPreviewModal] = useState<boolean>(false);
  const [importPreviewRows, setImportPreviewRows] = useState<ImportPreviewRow[]>([]);
  const [recordsReadyToImport, setRecordsReadyToImport] = useState<RecordType[]>([]);
  const [importBaseRecords, setImportBaseRecords] = useState<RecordType[]>([]);
  const [hoveredPreviewId, setHoveredPreviewId] = useState<string | null>(null);
  const [filterResults, setFilterResults] = useState<number>(0);
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [debugMode, setDebugMode] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState<RecordType[]>([]);
  const [activeUsersFilter, setActiveUsersFilter] = useState<string | null>(null);
  const [uniqueProducers, setUniqueProducers] = useState<string[]>([]);
  const [uniqueCountries, setUniqueCountries] = useState<string[]>([]);
  const [activeProducerFilter, setActiveProducerFilter] = useState<string | null>(null);
  const [activeCountryFilter, setActiveCountryFilter] = useState<string | null>(null);
  const { role, producerInfo } = useAuth();
  const visibleColumns = columns.filter(column => !hiddenColumns.includes(column.toLowerCase()));

  useEffect(() => {
    const nextStats = {
      total: records.length,
      producers: collectionName.toLowerCase() === 'bikeframes' ? uniqueProducers.length : 0,
      countries: collectionName.toLowerCase() === 'users' ? uniqueCountries.length : 0
    };
    onStatsChange?.(nextStats);
  }, [collectionName, records.length, uniqueProducers.length, uniqueCountries.length, onStatsChange]);

  const importPreviewColumns = useMemo(() => {
    const keys = new Set<string>();
    importPreviewRows.forEach(item => {
      Object.keys(item.record).forEach(key => keys.add(key));
    });
    return Array.from(keys);
  }, [importPreviewRows]);

  // Opóźnione filtrowanie dla lepszej wydajności
  const debouncedFilter = useDebounce(filter, 250);
  const debouncedColumnFilters = useDebounce(columnFilters, 250);

  // Funkcje do obsługi zapisywania i wczytywania pozycji kolumn
  const saveColumnOrder = () => {
    localStorage.setItem(`columnOrder_${collectionName}`, JSON.stringify(columns));
    localStorage.setItem(`hiddenColumns_${collectionName}`, JSON.stringify(hiddenColumns));
    alert('Pozycje kolumn zostały zapisane!');
  };

  const toggleColumnVisibility = (column: string) => {
    const normalized = column.toLowerCase();
    setHiddenColumns(prev => {
      let next: string[];
      if (prev.includes(normalized)) {
        next = prev.filter(c => c !== normalized);
      } else {
        next = [...prev, normalized];
      }
      localStorage.setItem(`hiddenColumns_${collectionName}`, JSON.stringify(next));
      return next;
    });

    setColumnFilters(prev => {
      const next = { ...prev };
      delete next[normalized];
      return next;
    });
  };

  const applyScopeFilter = (source: RecordType[]): RecordType[] => {
    if (collectionName.toLowerCase() === "bikeframes") {
      if (!activeProducerFilter) return [];
      const selectedProducer = activeProducerFilter.toLowerCase();
      return source.filter(record => {
        const prodKey = Object.keys(record).find(k => k.toLowerCase() === "producent");
        return prodKey && String(record[prodKey]).toLowerCase() === selectedProducer;
      });
    }

    if (collectionName.toLowerCase() === "users") {
      if (activeCountryFilter && activeCountryFilter !== "ALL") {
        const selectedCountry = activeCountryFilter.toLowerCase();
        return source.filter(record => {
          const countryKey = Object.keys(record).find(k => k.toLowerCase() === "country");
          return countryKey && String(record[countryKey]).toLowerCase() === selectedCountry;
        });
      }

      if (activeUsersFilter === "LAST100") {
        return [...source]
          .sort((a, b) => getAddedAtTimestamp(b) - getAddedAtTimestamp(a))
          .slice(0, 100);
      }

      if (activeUsersFilter === "TODAY") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return source.filter(record => {
          const ts = getAddedAtTimestamp(record);
          return ts >= today.getTime() && ts < tomorrow.getTime();
        });
      }

      if (activeUsersFilter === "YESTERDAY") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return source.filter(record => {
          const ts = getAddedAtTimestamp(record);
          return ts >= yesterday.getTime() && ts < today.getTime();
        });
      }

      if (activeCountryFilter === "ALL" || activeUsersFilter === "ALL") {
        return source;
      }

      return [];
    }

    return source;
  };

  // Główna logika filtrowania - uruchamia się gdy zmienia się filter lub columnFilters
  useEffect(() => {
    // Stabilna reprezentacja obiektu filtrów, żeby zależności działały poprawnie
    const debouncedColumnFiltersString = JSON.stringify(debouncedColumnFilters || {});

    // Parsujemy i normalizujemy klucze filtrów na lowercase
    const parsedColumnFilters: { [k: string]: string } = {};
    try {
      const tmp = JSON.parse(debouncedColumnFiltersString);
      Object.entries(tmp).forEach(([k, v]) => {
        if (typeof v === "string" && v.trim() !== "") {
          parsedColumnFilters[k.toLowerCase()] = v;
        }
      });
    } catch (err) {
      // Jeśli parsowanie się nie powiedzie, traktujemy jako brak filtrów kolumn
    }

    const hasGlobalFilter = debouncedFilter.trim().length > 0;
    const hasColumnFilter = Object.values(parsedColumnFilters).some(v => v.trim().length > 0);
    setIsFiltering(hasGlobalFilter || hasColumnFilter);

    const scopedRecords = applyScopeFilter(records);

    // Jeśli nie ma filtrów, pokaż dane tylko w aktualnym zakresie (np. wybrana marka)
    if (!hasGlobalFilter && !hasColumnFilter) {
      setFilteredData(scopedRecords.map(r => ({ ...r, id: String(r.id ?? "") })));
      setFilterResults(scopedRecords.length);
      if (debugMode) console.log("Brak aktywnych filtrów - pokazuję rekordy z aktualnego zakresu");
      return;
    }

    // Funkcja do sprawdzania czy rekord pasuje do filtra
    const matchesFilter = (record: RecordType): boolean => {
      if (hasGlobalFilter) {
        const filterLower = debouncedFilter.toLowerCase().trim();
        let foundMatch = false;
        for (const key in record) {
          if (key !== 'id') {
            const formattedValue = formatValue(record[key]).toLowerCase();
            if (formattedValue.includes(filterLower)) {
              foundMatch = true;
              break;
            }
          }
        }
        if (!foundMatch) return false;
      }

      if (hasColumnFilter) {
        for (const [columnRaw, filterValue] of Object.entries(parsedColumnFilters)) {
          if (!filterValue || !filterValue.trim()) continue;
          const column = columnRaw.toLowerCase(); // upewniamy się, że korzystamy z lowercase

          // jeśli rekord nie ma tej kolumny - nie pasuje
          // obsługa: rekord może mieć klucz z inną wielkością liter - próbujemy znaleźć odpowiednik
          const recordHasColumn = Object.prototype.hasOwnProperty.call(record, column) ||
            Object.keys(record).some(k => k.toLowerCase() === column);

          if (!recordHasColumn) return false;

          // Bezpieczne pobranie wartości (najpierw column, potem próbka przez case-insensitive)
          const rawKey = Object.keys(record).find(k => k.toLowerCase() === column) ?? column;
          const formattedValue = formatValue(record[rawKey]).toLowerCase();
          const columnFilterLower = filterValue.toLowerCase().trim();
          if (!formattedValue.includes(columnFilterLower)) return false;
        }
      }

      return true;
    };

    try {
      const filteredRecords = scopedRecords.filter(matchesFilter);

      const sortedRecords = sortConfig
        ? [...filteredRecords].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];
            if (aValue === undefined || bValue === undefined) return 0;
            if (typeof aValue === "number" && typeof bValue === "number") {
              return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
            }
            return sortConfig.direction === "asc"
              ? String(aValue).localeCompare(String(bValue))
              : String(bValue).localeCompare(String(aValue));
          })
        : filteredRecords;

      // Ustawiamy always nową tablicę i normalizujemy id do stringa
      const normalized = sortedRecords.map(r => ({ ...r, id: String(r.id ?? "") }));
      setFilteredData(normalized);
      setFilterResults(normalized.length);

      if (debugMode) {
        console.log("Wynik filtrowania:", {
          totalRecords: scopedRecords.length,
          filteredRecords: normalized.length,
          filter: debouncedFilter,
          columnFilters: parsedColumnFilters,
          isFiltering: hasGlobalFilter || hasColumnFilter
        });
      }
    } catch (error) {
      console.error("Błąd podczas filtrowania:", error);
      setFilteredData([]);
      setFilterResults(0);
    }
    // Uwaga: używamy stringa filtrow kolumn jako zależności żeby uniknąć problemów z porównaniem referencji obiektu
  }, [records, debouncedFilter, JSON.stringify(debouncedColumnFilters), sortConfig, debugMode, activeProducerFilter, activeCountryFilter, activeUsersFilter, collectionName]);

  // Efekt resetujący stan przy zmianie kolekcji
  useEffect(() => {
    // Reset stanów przy zmianie kolekcji
    setRecords([]);
    setFilteredData([]);
    setFilter("");
    setColumnFilters({});
    setSortConfig(null);
    setColumns([]);
    setHiddenColumns([]);
    setLoading(true);
    setError("");
    setEditingRecord(null);
    setActionMenuOpen(null);
    setActiveProducerFilter(null);
    setActiveCountryFilter(null);
    setActiveUsersFilter(null);

    fetchRecords(false);

    if (debugMode) {
      console.log(`Zmiana kolekcji na: ${collectionName} - zresetowano stany`);
    }
  }, [collectionName]);

  const fetchRecords = async (forceRefresh: boolean = false) => {
    try {
      console.log(`Pobieranie danych z kolekcji: ${collectionName}`);
      const cached = await getCachedCollection(collectionName, {
        maxAgeMs: 5 * 60 * 1000,
        forceRefresh
      });
      console.log(`Pobrano ${cached.length} dokumentów`);

      if (cached.length === 0) {
        setRecords([]);
        setFilteredData([]);
        setColumns([]);
        setError("");
        setLoading(false);
        return;
      }

      // Najpierw zbieramy wszystkie możliwe nazwy kolumn ze wszystkich dokumentów,
      // zachowując oryginalną wielkość liter (pierwsze napotkane wystąpienie).
      const allColumnsMap = new Map<string, string>();
      cached.forEach(record => {
        Object.keys(record).forEach(key => {
          if (key.toLowerCase() === "id") return;
          const normalized = key.toLowerCase();
          if (!allColumnsMap.has(normalized)) {
            allColumnsMap.set(normalized, key);
          }
        });
      });

      // Sortujemy kolumny alfabetycznie lub używamy zapisanej kolejności
      let sortedColumns = Array.from(allColumnsMap.values()).sort((a, b) => a.localeCompare(b));

      // Sprawdzamy, czy mamy zapisaną kolejność kolumn
      const savedOrder = localStorage.getItem(`columnOrder_${collectionName}`);
      if (savedOrder) {
        const savedColumns = (JSON.parse(savedOrder) as string[]).map(col => col.toLowerCase());
        const existingSavedColumns = savedColumns
          .map(col => allColumnsMap.get(col))
          .filter((col): col is string => Boolean(col));
        const newColumns = Array.from(allColumnsMap.entries())
          .filter(([normalized]) => !savedColumns.includes(normalized))
          .map(([, original]) => original);
        sortedColumns = [...existingSavedColumns, ...newColumns];
      }

      console.log("Ustawienie kolumn:", sortedColumns);
      setColumns(sortedColumns);

      const savedHidden = localStorage.getItem(`hiddenColumns_${collectionName}`);
      let hidden: string[] = [];
      if (savedHidden) {
        try {
          hidden = (JSON.parse(savedHidden) as string[])
            .map(col => String(col).toLowerCase())
            .filter(col => allColumnsMap.has(col));
        } catch (err) {
          hidden = [];
        }
      }
      setHiddenColumns(hidden);

      // Tworzymy rekordy i upewniamy się że id jest stringiem
      let data = cached.map(docData => {
        const record: RecordType = { id: String(docData.id) };
        sortedColumns.forEach(column => {
          // Szukamy klucza w oryginalnych danych niezależnie od wielkości liter
          const foundKey = Object.keys(docData).find(k => k.toLowerCase() === column.toLowerCase());
          record[column] = foundKey !== undefined ? docData[foundKey] : null;
        });
        return record;
      });

      // Jeśli producent - filtruj tylko swoje rekordy
      if (collectionName.toLowerCase() === 'bikeframes' && role === 'producer' && producerInfo?.brand) {
        data = data.filter(r => {
          const prodKey = Object.keys(r).find(k => k.toLowerCase() === 'producent');
          return prodKey && String(r[prodKey]).toUpperCase() === String(producerInfo.brand).toUpperCase();
        });
      }

      console.log(`Przetworzono ${data.length} rekordów z bazy`);

      // Dla kolekcji users - sortuj domyślnie po dacie dodania (addedAt) malejąco (najnowsze na górze)
      let sortedData = data;
      if (collectionName.toLowerCase() === 'users') {
        sortedData = [...data].sort((a, b) => getAddedAtTimestamp(b) - getAddedAtTimestamp(a));
        const preferredDateColumns = ["addedAt", "createdAt", "created_at", "createdOn", "created_on", "date", "timestamp"];
        const preferredDateColumn = preferredDateColumns.find(column =>
          sortedColumns.some(col => col.toLowerCase() === column.toLowerCase())
        ) ?? sortedColumns[0] ?? "addedAt";
        setSortConfig({ key: preferredDateColumn, direction: 'desc' });
      }

      setRecords(sortedData.map(r => ({ ...r, id: String(r.id ?? "") })));
      // Dla bikeFrames nie pokazuj danych, dopóki nie wybierzesz producenta
      if (collectionName.toLowerCase() === 'bikeframes') {
        setFilteredData([]);
        setFilterResults(0);
      } else {
        setFilteredData(sortedData.map(r => ({ ...r, id: String(r.id ?? "") })));
      }
      setError("");

      // Pobierz unikalne producenty (dla bikeFrames) lub kraje (dla users)
      if (collectionName.toLowerCase() === 'bikeframes') {
        const producers = new Set<string>();
        cached.forEach(record => {
          const val = record.producent || record.producer;
          if (val) producers.add(String(val));
        });
        const producersList = Array.from(producers).sort();
        setUniqueProducers(producersList);

        if (activeProducerFilter) {
          const normalizedActiveProducer = activeProducerFilter.toLowerCase();
          const activeStillExists = producersList.some(p => p.toLowerCase() === normalizedActiveProducer);
          if (activeStillExists) {
            const filtered = data.filter(record => {
              const prodKey = Object.keys(record).find(k => k.toLowerCase() === 'producent');
              return prodKey && String(record[prodKey]).toLowerCase() === normalizedActiveProducer;
            });
            setFilteredData(filtered);
            setFilterResults(filtered.length);
          } else {
            setActiveProducerFilter(null);
            setFilteredData([]);
            setFilterResults(0);
          }
        } else {
          setFilteredData([]);
          setFilterResults(0);
        }
      } else if (collectionName.toLowerCase() === 'users') {
        const countries = new Set<string>();
        cached.forEach(record => {
          const val = record.country;
          if (val) countries.add(String(val));
        });
        setUniqueCountries(Array.from(countries).sort());
        setActiveCountryFilter(null);
      }
    } catch (err) {
      console.error("Błąd podczas pobierania danych:", err);
      setError(`Nie udało się pobrać danych z kolekcji '${collectionName}'`);
      setRecords([]);
      setFilteredData([]);
      setColumns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    setLoading(true);
    setError("");
    await fetchRecords(true);
  };

  // Funkcja do eksportu danych do CSV
  const exportToCSV = () => {
    const rowsToExport = (() => {
      const baseRows = collectionName.toLowerCase() === 'bikeframes' && activeProducerFilter
        ? filteredData.length > 0
          ? filteredData
          : records.filter(record => {
              const prodKey = Object.keys(record).find(k => k.toLowerCase() === 'producent');
              return prodKey && String(record[prodKey]).toLowerCase() === activeProducerFilter.toLowerCase();
            })
        : records;

      if (collectionName.toLowerCase() === 'bikeframes') {
        return [...baseRows].sort((a, b) => {
          const aProducer = String(Object.keys(a).find(k => k.toLowerCase() === 'producent') ? a[Object.keys(a).find(k => k.toLowerCase() === 'producent')!] : '').trim().toLowerCase();
          const bProducer = String(Object.keys(b).find(k => k.toLowerCase() === 'producent') ? b[Object.keys(b).find(k => k.toLowerCase() === 'producent')!] : '').trim().toLowerCase();
          return aProducer.localeCompare(bProducer);
        });
      }

      return baseRows;
    })();

    if (rowsToExport.length === 0) return;

    const headers = visibleColumns.length > 0 ? visibleColumns : Object.keys(rowsToExport[0] ?? {});

    const csvRows = [
      headers.join(','),
      ...rowsToExport.map(record =>
        headers.map(header => {
          const value = formatValue(record[header]);
          return /[",]/.test(value)
            ? `"${value.replace(/"/g, '""')}"`
            : value;
        }).join(',')
      )
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${collectionName}_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Zmieniona funkcja wyboru pliku - teraz tylko zapisuje wybrany plik
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setSelectedFile(file || null);
    setImportProgress(0);
    setShowImportPreviewModal(false);
    setImportPreviewRows([]);
    setRecordsReadyToImport([]);
    setImportBaseRecords([]);
    setError("");
  };

  const normalizeIdPart = (value: unknown): string =>
    String(value ?? "").trim().toUpperCase().replace(/\s+/g, "");

  const makeFrameDocId = (producent: unknown, model: unknown, rok: unknown, rozmiar: unknown): string =>
    [
      normalizeIdPart(producent),
      normalizeIdPart(model),
      normalizeIdPart(rok),
      normalizeIdPart(rozmiar)
    ].join("_").replace(/\//g, "-");

  const getFieldValueCaseInsensitive = (rec: RecordType, field: string): unknown => {
    const key = Object.keys(rec).find(k => k.toLowerCase() === field.toLowerCase());
    return key ? rec[key] : "";
  };

  const buildFrameKey = (rec: RecordType): string => {
    return makeFrameDocId(
      getFieldValueCaseInsensitive(rec, "producent"),
      getFieldValueCaseInsensitive(rec, "model"),
      getFieldValueCaseInsensitive(rec, "rok"),
      getFieldValueCaseInsensitive(rec, "rozmiar")
    );
  };

  const toRecordToSave = (record: RecordType, docId: string): RecordType => {
    const recordToSave: Record<string, any> = { id: docId };
    Object.keys(record).forEach(key => {
      if (record[key] !== undefined && record[key] !== null) {
        if (key.toLowerCase() === "id") {
          recordToSave[key] = String(record[key]);
        } else {
          recordToSave[key] = record[key];
        }
      }
    });
    return recordToSave;
  };

  const parseCSV = (csvText: string) => {
    const lines = csvText.split(/\r?\n/).filter(line => line.trim().length > 0);
    if (lines.length === 0) throw new Error("Plik CSV jest pusty");

    const detectDelimiter = (line: string): string => {
      let commas = 0;
      let semicolons = 0;
      let tabs = 0;
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            i++;
          } else {
            inQuotes = !inQuotes;
          }
          continue;
        }
        if (inQuotes) continue;
        if (char === ',') commas++;
        if (char === ';') semicolons++;
        if (char === '\t') tabs++;
      }

      if (semicolons >= commas && semicolons >= tabs && semicolons > 0) return ';';
      if (tabs >= commas && tabs >= semicolons && tabs > 0) return '\t';
      return ',';
    };

    const delimiter = detectDelimiter(lines[0]);

    function parseCSVLine(line: string, separator: string): string[] {
      const result: string[] = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === separator && !inQuotes) {
          result.push(current);
          current = "";
        } else {
          current += char;
        }
      }
      result.push(current);
      return result;
    }

    const cleanHeader = (header: string): string =>
      header
        .replace(/^\uFEFF/, "")
        .trim()
        .replace(/^"|"$/g, "");

    const toCanonicalHeader = (header: string): string => {
      const normalized = cleanHeader(header).toLowerCase();
      const aliases: Record<string, string> = {
        producer: "producent",
        producent: "producent",
        brand: "producent",
        model: "model",
        rok: "rok",
        year: "rok",
        rozmiar: "rozmiar",
        size: "rozmiar"
      };
      return aliases[normalized] || normalized;
    };

    const originalHeaders = parseCSVLine(lines[0], delimiter).map(cleanHeader);
    const canonicalHeaders = originalHeaders.map(toCanonicalHeader);
    const requiredHeaders = ["producent", "model", "rok", "rozmiar"];
    const missingHeaders = requiredHeaders.filter(h => !canonicalHeaders.includes(h));
    if (missingHeaders.length > 0) {
      throw new Error(
        `Brak wymaganych kolumn: ${missingHeaders.join(", ")}. ` +
        `Wymagane kolumny: producent, model, rok, rozmiar.`
      );
    }

    const idIndex = originalHeaders.findIndex(h => h.toLowerCase() === "id");
    const dataHeaders = originalHeaders.filter((_, idx) => idx !== idIndex);
    const dataCanonicalHeaders = canonicalHeaders.filter((_, idx) => idx !== idIndex);

    const rows: RecordType[] = [];
    for (let i = 1; i < lines.length; i++) {
      let values = parseCSVLine(lines[i], delimiter);
      values = values.map(v => v.trim().replace(/^"|"$/g, ""));
      if (values.length === 0 || values.every(v => v === "")) continue;
      if (idIndex !== -1) values.splice(idIndex, 1);

      const row: RecordType = {};
      dataHeaders.forEach((header, idx) => {
        let value: string | number | null = values[idx] ?? null;
        const lowerHeader = dataCanonicalHeaders[idx]?.toLowerCase() ?? header.toLowerCase();
        if (
          !["producent", "model", "rozmiar", "id"].includes(lowerHeader) &&
          value !== null &&
          value !== "" &&
          !isNaN(Number(value))
        ) {
          value = Number(value);
        }
        row[header] = value;
      });
      rows.push(row);
    }

    return { rows };
  };

  const performImportSave = async () => {
    if (recordsReadyToImport.length === 0) {
      setError("Brak rekordów gotowych do zapisu.");
      setShowImportPreviewModal(false);
      return;
    }

    try {
      setLoading(true);
      setIsImporting(true);
      setImportProgress(0);
      setError("");

      const refreshedExisting = await getCachedCollection(collectionName, {
        forceRefresh: true,
        maxAgeMs: 0
      });
      const existingFrameKeys = new Set<string>(
        refreshedExisting
          .map(buildFrameKey)
          .filter(key => key !== "___")
      );

      let addedCount = 0;
      let skippedDuplicateCount = 0;
      const skipped: RecordType[] = [];
      const addedRecords: RecordType[] = [];
      const totalRows = recordsReadyToImport.length;

      for (let i = 0; i < recordsReadyToImport.length; i++) {
        const record = recordsReadyToImport[i];
        const docId = makeFrameDocId(
          getFieldValueCaseInsensitive(record, "producent"),
          getFieldValueCaseInsensitive(record, "model"),
          getFieldValueCaseInsensitive(record, "rok"),
          getFieldValueCaseInsensitive(record, "rozmiar")
        );
        setImportProgress(Math.round(((i + 1) / totalRows) * 100));

        if (existingFrameKeys.has(docId)) {
          skipped.push({ ...record, _skipReason: "Duplikat już istnieje w kolekcji" });
          skippedDuplicateCount++;
          continue;
        }

        try {
          const recordToSave = toRecordToSave(record, docId);
          await setDoc(doc(collection(db, collectionName), docId), recordToSave);
          addedRecords.push({ ...recordToSave, id: docId });
          existingFrameKeys.add(docId);
          addedCount++;
        } catch (addError) {
          skipped.push({
            ...record,
            _skipReason: `Błąd zapisu: ${addError instanceof Error ? addError.message : String(addError)}`
          });
        }
      }

      setSkippedRecords(skipped);
      setShowSkippedModal(skipped.length > 0);
      setImportProgress(100);

      const merged = new Map<string, RecordType>();
      importBaseRecords.forEach(record => {
        if (record.id) merged.set(String(record.id), record);
      });
      addedRecords.forEach(record => {
        if (record.id) merged.set(String(record.id), record);
      });
      const mergedRecords = Array.from(merged.values()).map(record => ({
        ...record,
        id: String(record.id)
      }));
      setCachedCollection(collectionName, mergedRecords);
      await fetchRecords(false);

      alert(
        `Import CSV do kolekcji ${collectionName} zakończony:\n` +
        `- Dodanych rekordów: ${addedCount}\n` +
        `- Pominiętych duplikatów: ${skippedDuplicateCount}\n` +
        `- Rekordów po podglądzie: ${recordsReadyToImport.length}`
      );

      // Po zaimportowaniu nowych ram odśwież cache katalogu rowerów w głównej aplikacji
      // (public/cache/bike-models.json), żeby nowe rowery od razu były widoczne.
      if (collectionName === 'bikeFrames' && addedCount > 0) {
        try {
          const apiBase = getApiBaseUrl();
          await fetch(`${apiBase}/api/bike-models-cache`, {
            method: 'POST',
            credentials: 'include',
          });
        } catch (refreshError) {
          console.warn('[DataTable] Nie udało się odświeżyć cache bike-models:', refreshError);
        }
      }

      setShowImportPreviewModal(false);
      setSelectedFile(null);
      setImportPreviewRows([]);
      setRecordsReadyToImport([]);
      setImportBaseRecords([]);
    } catch (err) {
      setError(`Wystąpił błąd podczas zapisu importu: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsImporting(false);
      setLoading(false);
    }
  };

  // Import uruchamia teraz tylko podgląd; zapis jest wykonywany dopiero po potwierdzeniu.
  const startImport = async () => {
    if (!selectedFile) return;

    try {
      setLoading(true);
      setImportProgress(0);
      setSkippedRecords([]);
      setError(""); // Czyszczenie błędów przed rozpoczęciem importu

      const text = await selectedFile.text();

    try {
      const { rows } = parseCSV(text);

      // Przed importem pobierz świeży stan kolekcji (jednorazowo).
      const existingRecords: RecordType[] = await getCachedCollection(collectionName, {
        forceRefresh: true,
        maxAgeMs: 0
      });

      const existingFrameKeys = new Set<string>(
        existingRecords
          .map(buildFrameKey)
          .filter(key => key !== "___")
      );

      const previewRows: ImportPreviewRow[] = [];
      const readyToImport: RecordType[] = [];
      const seenRecordsInCsv: Set<string> = new Set();
      const totalRows = rows.length;

      for (let i = 0; i < rows.length; i++) {
        const record = rows[i];
        if (totalRows > 0) {
          setImportProgress(Math.round(((i + 1) / totalRows) * 100));
        }

        // Sprawdź wymagane pola (z małej litery)
        const producent = getFieldValueCaseInsensitive(record, "producent");
        const model = getFieldValueCaseInsensitive(record, "model");
        const rok = getFieldValueCaseInsensitive(record, "rok");
        const rozmiar = getFieldValueCaseInsensitive(record, "rozmiar");
        const docId = makeFrameDocId(producent, model, rok, rozmiar);

        if (!producent || !model || !rok || !rozmiar) {
          previewRows.push({
            docId,
            record,
            status: "invalid",
            reason: "Brak wymaganych pól (producent/model/rok/rozmiar)"
          });
          continue;
        }

        // Sprawdź duplikaty w pliku CSV
        if (seenRecordsInCsv.has(docId)) {
          previewRows.push({
            docId,
            record,
            status: "duplicateInFile",
            reason: "Duplikat w tym samym pliku CSV"
          });
          continue;
        }
        seenRecordsInCsv.add(docId);

        // Sprawdź duplikaty w bazie
        if (existingFrameKeys.has(docId)) {
          previewRows.push({
            docId,
            record,
            status: "duplicateInCollection",
            reason: "Duplikat już istnieje w kolekcji"
          });
        } else {
          previewRows.push({
            docId,
            record,
            status: "ready"
          });
          readyToImport.push(record);
        }
      }

      setImportProgress(100);
      setImportPreviewRows(previewRows);
      setRecordsReadyToImport(readyToImport);
      setImportBaseRecords(existingRecords);
      setShowImportPreviewModal(true);
    } catch (parseError) {
      setError(`Nieprawidłowy format pliku CSV: ${parseError instanceof Error ? parseError.message : String(parseError)}`);
      console.error("Błąd parsowania CSV:", parseError);
    }
  } catch (err) {
    setError(`Wystąpił błąd podczas importu pliku CSV: ${err instanceof Error ? err.message : String(err)}`);
    console.error("Błąd importu CSV:", err);
  } finally {
    setIsImporting(false);
    setLoading(false);
  }
  };

  // Dodajemy useEffect na końcu komponentu, aby czyścić błędy
  useEffect(() => {
    // Czyszczenie komunikatu o błędzie po zmianie pliku
    if (selectedFile) {
      setError("");
    }
  }, [selectedFile]);

  // Aktualizacja filtrowania dla konkretnej kolumny
  const handleColumnFilterChange = (column: string, value: string) => {
    // Używamy zawsze lowercase jako klucza filtra (unika problemów z wielkością liter np. bikeFrames)
    const key = column.toLowerCase();
    setColumnFilters(prev => {
      const next = { ...prev, [key]: value };
      // usuń puste klucze, żeby nie zostawiać śmieci
      if (next[key] === "") {
        delete next[key];
      }
      return next;
    });
  };

  // Resetowanie wszystkich filtrów kolumn
  const resetColumnFilters = () => {
    setColumnFilters({});
    setFilter("");
    // Wyraźnie ustawiamy filteredData na kopię records
    setFilteredData(records.map(r => ({ ...r, id: String(r.id ?? "") })));
    setFilterResults(records.length);
    setIsFiltering(false);
    if (debugMode) console.log("Zresetowano filtry - pokazuję wszystkie rekordy");
  };

  // Funkcja sortująca dane (przeniesiona do renderowania)
  const getSortedData = (data: RecordType[]) => {
    if (!sortConfig) return data;
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue === undefined || bValue === undefined) return 0;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  };

  // Funkcje dla przycisków users filter
  const handleUsersAll = () => {
    setActiveUsersFilter('ALL');
    setActiveCountryFilter('ALL');
    setFilteredData(records);
    setFilterResults(records.length);
  };

  const handleUsersLast100 = () => {
    setActiveUsersFilter('LAST100');
    setActiveCountryFilter('ALL');
    const sorted = [...records].sort((a, b) => getAddedAtTimestamp(b) - getAddedAtTimestamp(a));
    const last100 = sorted.slice(0, 100);
    setFilteredData(last100);
    setFilterResults(last100.length);
  };

  const handleUsersToday = () => {
    setActiveUsersFilter('TODAY');
    setActiveCountryFilter('ALL');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const filtered = records.filter(record => {
      const ts = getAddedAtTimestamp(record);
      if (!ts) return false;
      return ts >= today.getTime() && ts < tomorrow.getTime();
    });

    setFilteredData(filtered);
    setFilterResults(filtered.length);
  };

  const handleUsersYesterday = () => {
    setActiveUsersFilter('YESTERDAY');
    setActiveCountryFilter('ALL');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const filtered = records.filter(record => {
      const ts = getAddedAtTimestamp(record);
      if (!ts) return false;
      return ts >= yesterday.getTime() && ts < today.getTime();
    });

    setFilteredData(filtered);
    setFilterResults(filtered.length);
  };

  // Funkcje dla filtrowania producentów (bikeFrames)
  const handleProducerFilter = (producer: string) => {
    if (activeProducerFilter === producer) {
      setActiveProducerFilter(null);
      setFilteredData(records);
      setFilterResults(records.length);
    } else {
      setActiveProducerFilter(producer);
      const filtered = records.filter(record => {
        const prodKey = Object.keys(record).find(k => k.toLowerCase() === 'producent');
        return prodKey && String(record[prodKey]).toLowerCase() === producer.toLowerCase();
      });
      setFilteredData(filtered);
      setFilterResults(filtered.length);
    }
  };

  // Funkcje dla filtrowania krajów (users)
  const handleCountryFilter = (country: string) => {
    if (country === 'ALL') {
      setActiveCountryFilter('ALL');
      setActiveUsersFilter('ALL');
      setFilteredData(records);
      setFilterResults(records.length);
      return;
    }
    if (activeCountryFilter === country) {
      setActiveCountryFilter(null);
      setFilteredData(records);
      setFilterResults(records.length);
    } else {
      setActiveUsersFilter(null);
      setActiveCountryFilter(country);
      const filtered = records.filter(record => {
        const countryKey = Object.keys(record).find(k => k.toLowerCase() === 'country');
        return countryKey && String(record[countryKey]).toLowerCase() === country.toLowerCase();
      });
      setFilteredData(filtered);
      setFilterResults(filtered.length);
    }
  };

  // Obsługa kliknięcia nagłówka kolumny
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  // Funkcje do obsługi przeciągania kolumn
  const handleDragStart = (column: string) => {
    setDraggedColumn(column);
  };

  const handleDragOver = (e: React.DragEvent, column: string) => {
    e.preventDefault();
    setDropTargetColumn(column);
  };

  const handleDrop = () => {
    if (!draggedColumn || !dropTargetColumn) return;

    const draggedIndex = columns.indexOf(draggedColumn);
    const dropIndex = columns.indexOf(dropTargetColumn);

    const newColumns = [...columns];
    newColumns.splice(draggedIndex, 1);
    newColumns.splice(dropIndex, 0, draggedColumn);

    setColumns(newColumns);
    setDraggedColumn(null);
    setDropTargetColumn(null);
  };

  // Funkcja do usuwania rekordu
  const handleDeleteRecord = async (recordId: string) => {
    if (!recordId) {
      console.error("Brak ID rekordu do usunięcia");
      return;
    }

    if (window.confirm("Czy na pewno chcesz usunąć ten rekord?")) {
      try {
        setLoading(true);
        console.log(`Próba usunięcia rekordu o ID: ${recordId}`);

        const docRef = doc(db, collectionName, recordId);
        await deleteDoc(docRef);
        removeCachedRecord(collectionName, recordId);
        console.log(`Usunięto rekord o ID: ${recordId}`);

        // Aktualizacja lokalnych danych - usuń rekord z tablicy
        setRecords(prevRecords => prevRecords.filter(rec => rec.id !== recordId));
        setActionMenuOpen(null);
      } catch (err) {
        console.error("Błąd podczas usuwania rekordu:", err);
        setError("Wystąpił błąd podczas usuwania rekordu: " + (err instanceof Error ? err.message : String(err)));
        // Odświeżenie danych po błędzie
        await fetchRecords(true);
      } finally {
        setLoading(false);
      }
    }
  };

  // Funkcja do zapisywania zmian w rekordzie - zmodyfikowana
  const handleSaveRecord = async (updatedRecord: RecordType) => {
    try {
      setLoading(true);

      const isBikeCollection = collectionName.toLowerCase() === "bikeframes";
      let targetDocId = String(updatedRecord.id ?? "").trim();
      const recordToUpdate: Record<string, any> = {};

      if (isBikeCollection) {
        const { producent, model, rok, rozmiar } = updatedRecord;

        if (!producent || !model || !rok || !rozmiar) {
          setError("Brakujące dane ramy. Wymagane pola: producent, model, rok, rozmiar.");
          setLoading(false);
          return;
        }

        targetDocId = [
          String(producent).toUpperCase().replace(/\s+/g, ''),
          String(model).toUpperCase().replace(/\s+/g, ''),
          String(rok).toUpperCase().replace(/\s+/g, ''),
          String(rozmiar).toUpperCase().replace(/\s+/g, '')
        ].join('_');
      } else if (!targetDocId) {
        setError("Brak ID rekordu użytkownika.");
        setLoading(false);
        return;
      }

      recordToUpdate.id = targetDocId;

      Object.keys(updatedRecord).forEach(key => {
        if (key === "id" || updatedRecord[key] === undefined) return;

        if (isBikeCollection) {
          const lowerKey = key.toLowerCase();
          if (lowerKey === "producent" || lowerKey === "model" || lowerKey === "rozmiar") {
            recordToUpdate[key] = updatedRecord[key];
            return;
          }

          const value = updatedRecord[key];
          if (typeof value === "number") {
            recordToUpdate[key] = value;
          } else if (typeof value === "string" && value.trim() !== "") {
            const numValue = parseFloat(value);
            recordToUpdate[key] = Number.isNaN(numValue) ? value : numValue;
          } else {
            recordToUpdate[key] = value;
          }

          return;
        }

        recordToUpdate[key] = updatedRecord[key];
      });

      if (isBikeCollection) {
        if (recordToUpdate.producent) recordToUpdate.producent = recordToUpdate.producent.toUpperCase();
        if (recordToUpdate.model) recordToUpdate.model = recordToUpdate.model.toUpperCase();
        if (recordToUpdate.rozmiar) recordToUpdate.rozmiar = recordToUpdate.rozmiar.toUpperCase();

        if (recordToUpdate.rok !== undefined) {
          const rokValue = recordToUpdate.rok;
          if (typeof rokValue === 'string') {
            const numRok = parseFloat(rokValue);
            if (!isNaN(numRok)) {
              recordToUpdate.rok = numRok;
            }
          }
        }
      }

      const docRef = doc(db, collectionName, targetDocId);
      await setDoc(docRef, recordToUpdate);

      if (updatedRecord.id && String(updatedRecord.id) !== targetDocId) {
        await deleteDoc(doc(db, collectionName, String(updatedRecord.id)));
      }

      if (updatedRecord.id && String(updatedRecord.id) !== targetDocId) {
        removeCachedRecord(collectionName, String(updatedRecord.id));
      }
      upsertCachedRecord(collectionName, { ...recordToUpdate, id: targetDocId }, updatedRecord.id ? String(updatedRecord.id) : undefined);

      setRecords(prevRecords => {
        const next = prevRecords.filter(rec => rec.id !== updatedRecord.id && rec.id !== targetDocId);
        next.push({ ...recordToUpdate, id: targetDocId });
        return next;
      });

      setEditingRecord(null);
      await fetchRecords(false);
    } catch (err) {
      console.error("Błąd podczas aktualizacji rekordu:", err);

      // Bardziej opisowy komunikat błędu
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Wystąpił błąd podczas aktualizacji rekordu: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Poprawiona funkcja otwierająca okno edycji po kliknięciu wiersza
  const handleRowClick = (record: RecordType) => {
    console.log("🔵 Kliknięto wiersz:", record);

    try {
      // Utworzenie głębokiej kopii z bezpośrednim przypisaniem wszystkich pól
      const recordCopy = { ...record };
      Object.keys(record).forEach(key => {
        if (typeof record[key] === 'object' && record[key] !== null) {
          recordCopy[key] = JSON.parse(JSON.stringify(record[key]));
        }
      });

      console.log("🟢 Ustawiam editingRecord:", recordCopy);
      setEditingRecord(recordCopy);
      setActionMenuOpen(null); // Zamknij menu akcji jeśli jest otwarte
    } catch (error) {
      console.error("❌ Błąd podczas kopiowania rekordu:", error);
      // Awaryjne przypisanie bez głębokiej kopii
      setEditingRecord({ ...record });
    }
  };

  const openEditModal = (e: React.MouseEvent, record: RecordType) => {
    e.preventDefault();
    e.stopPropagation();

    if (!record || !record.id) {
      console.error("Nieprawidłowy rekord do edycji:", record);
      return;
    }

    setEditingRecord(JSON.parse(JSON.stringify(record)));
    setActionMenuOpen(null);
  };

  // Dodaj useEffect do śledzenia zmian editingRecord
  useEffect(() => {
    console.log("🟠 Stan editingRecord zmieniony:", editingRecord);
  }, [editingRecord]);

  if (loading && records.length === 0) return <div style={{ color: "#64ffda", fontSize: "18px", textAlign: "center", padding: "20px" }}>Ładowanie danych...</div>;
  if (error) return <div style={{ color: "red", fontSize: "18px", textAlign: "center", padding: "20px" }}>{error}</div>;

  return (
    <div style={{ padding: "0 5px 20px 5px" }}>
      {/* Przyciski akcji - bardziej kompaktowe */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px", flexWrap: "wrap", gap: "5px" }}>
        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
          <button
            onClick={saveColumnOrder}
            style={{
              padding: "6px 10px",
              backgroundColor: "#64ffda",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "12px"
            }}
          >
            Zapisz kolumny
          </button>
          <button
            onClick={handleManualRefresh}
            disabled={loading || isImporting}
            style={{
              padding: "6px 10px",
              backgroundColor: "#64ffda",
              border: "none",
              borderRadius: "5px",
              cursor: loading || isImporting ? "not-allowed" : "pointer",
              fontSize: "12px",
              opacity: loading || isImporting ? 0.6 : 1
            }}
          >
            Odśwież bazę/cache
          </button>
          <details style={{ color: "#e6f1ff" }}>
            <summary style={{ cursor: "pointer", fontSize: "12px", padding: "6px 10px", backgroundColor: "#1e2d45", borderRadius: "5px" }}>
              Kolumny (pokaż/ukryj)
            </summary>
            <div style={{ marginTop: "6px", backgroundColor: "#112240", border: "1px solid #233554", borderRadius: "6px", padding: "8px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "6px", minWidth: "280px", maxWidth: "560px" }}>
              {columns.map(column => {
                const checked = !hiddenColumns.includes(column.toLowerCase());
                return (
                  <label key={`visibility-${column}`} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleColumnVisibility(column)}
                    />
                    <span>{column}</span>
                  </label>
                );
              })}
            </div>
          </details>
          <button
            onClick={exportToCSV}
            style={{
              padding: "6px 10px",
              backgroundColor: "#64ffda",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "12px"
            }}
          >
            Eksport CSV
          </button>

          {/* Import CSV - przywrócone elementy */}
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{
                padding: "4px",
                fontSize: "12px",
                maxWidth: "180px"
              }}
            />
            <button
              onClick={startImport}
              disabled={!selectedFile || isImporting}
              style={{
                padding: "6px 10px",
                backgroundColor: selectedFile ? "#64ffda" : "#3a506b",
                color: selectedFile ? "#0a192f" : "#8892b0",
                border: "none",
                borderRadius: "5px",
                cursor: selectedFile ? "pointer" : "not-allowed",
                fontSize: "12px"
              }}
            >
              {isImporting ? `Import... ${importProgress}%` : "Importuj CSV (podgląd)"}
            </button>
          </div>

          {/* Przycisk trybu debugowania */}
          <button
            onClick={() => setDebugMode(!debugMode)}
            style={{
              padding: "6px 10px",
              backgroundColor: debugMode ? "#ff3d00" : "#3a506b",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "12px"
            }}
          >
            {debugMode ? "Debugowanie ✓" : "Debugowanie"}
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label style={{ color: "#64ffda", marginRight: "5px", fontSize: "12px" }}>Filtr:</label>
          <input
            type="text"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Szukaj..."
            style={{
              padding: "6px",
              borderRadius: "5px",
              border: isFiltering ? "2px solid #64ffda" : "1px solid #64ffda",
              width: "150px",
              fontSize: "12px",
              backgroundColor: isFiltering ? "rgba(100, 255, 218, 0.1)" : "#1e2d45"
            }}
          />
          {isFiltering && (
            <button
              onClick={resetColumnFilters}
              style={{
                backgroundColor: "#ff3d00",
                color: "white",
                border: "none",
                borderRadius: "5px",
                padding: "6px 10px",
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              ✖ Reset
            </button>
          )}
        </div>
      </div>

      {/* Przyciski filtrowania dla users */}
      {collectionName.toLowerCase() === 'users' && (
        <div style={{
          display: "flex",
          gap: "8px",
          marginBottom: "10px",
          flexWrap: "wrap",
          alignItems: "center"
        }}>
          <button
            onClick={handleUsersAll}
            style={{
              padding: "6px 12px",
              backgroundColor: activeUsersFilter === 'ALL' ? "#64ffda" : "#1a2c44",
              color: activeUsersFilter === 'ALL' ? "#0a192f" : "#e6f1ff",
              border: "1px solid #233554",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: activeUsersFilter === 'ALL' ? "bold" : "normal"
            }}
          >
            Wszystkie rekordy
          </button>
          <button
            onClick={handleUsersLast100}
            style={{
              padding: "6px 12px",
              backgroundColor: activeUsersFilter === 'LAST100' ? "#64ffda" : "#1a2c44",
              color: activeUsersFilter === 'LAST100' ? "#0a192f" : "#e6f1ff",
              border: "1px solid #233554",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: activeUsersFilter === 'LAST100' ? "bold" : "normal"
            }}
          >
            Ostatnich 100
          </button>
          <button
            onClick={handleUsersYesterday}
            style={{
              padding: "6px 12px",
              backgroundColor: activeUsersFilter === 'YESTERDAY' ? "#64ffda" : "#1a2c44",
              color: activeUsersFilter === 'YESTERDAY' ? "#0a192f" : "#e6f1ff",
              border: "1px solid #233554",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: activeUsersFilter === 'YESTERDAY' ? "bold" : "normal"
            }}
          >
            Wczorajsze
          </button>
          <button
            onClick={handleUsersToday}
            style={{
              padding: "6px 12px",
              backgroundColor: activeUsersFilter === 'TODAY' ? "#64ffda" : "#1a2c44",
              color: activeUsersFilter === 'TODAY' ? "#0a192f" : "#e6f1ff",
              border: "1px solid #233554",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: activeUsersFilter === 'TODAY' ? "bold" : "normal"
            }}
          >
            Dzisiejsze
          </button>
          {activeCountryFilter && activeCountryFilter !== "ALL" && (
            <span style={{
              color: "#64ffda",
              fontSize: "18px",
              fontWeight: "bold",
              letterSpacing: "0.08em",
              marginLeft: "10px"
            }}>
              {getCountryDisplayName(activeCountryFilter)}
            </span>
          )}
        </div>
      )}

      {/* Kafelki z producentami (bikeFrames) */}
      {collectionName.toLowerCase() === 'bikeframes' && uniqueProducers.length > 0 && (
        <div style={{
          display: "flex",
          gap: "8px",
          marginBottom: "10px",
          flexWrap: "wrap"
        }}>
          <span style={{
            color: "#64ffda",
            fontSize: "12px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center"
          }}>
            Wybierz producenta aby wyświetlić rekordy:
          </span>
          {uniqueProducers.map(producer => (
            <button
              key={producer}
              onClick={() => handleProducerFilter(producer)}
              style={{
                padding: "6px 12px",
                backgroundColor: activeProducerFilter === producer ? "#64ffda" : "#1a2c44",
                color: activeProducerFilter === producer ? "#0a192f" : "#e6f1ff",
                border: "1px solid #233554",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: activeProducerFilter === producer ? "bold" : "normal"
              }}
            >
              {producer}
            </button>
          ))}
        </div>
      )}

      {/* Kafelki z krajami (users) */}
      {collectionName.toLowerCase() === 'users' && uniqueCountries.length > 0 && (
        <div style={{
          display: "flex",
          gap: "8px",
          marginBottom: "10px",
          flexWrap: "wrap"
        }}>
          <span style={{
            color: "#64ffda",
            fontSize: "12px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center"
          }}>
            Wybierz kraj aby wyświetlić rekordy:
          </span>
          <button
            onClick={() => handleCountryFilter('ALL')}
            style={{
              padding: "6px 12px",
              backgroundColor: activeCountryFilter === 'ALL' ? "#64ffda" : "#1a2c44",
              color: activeCountryFilter === 'ALL' ? "#0a192f" : "#e6f1ff",
              border: "1px solid #233554",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: activeCountryFilter === 'ALL' ? "bold" : "normal"
            }}
          >
            ALL
          </button>
          {uniqueCountries.map(country => (
            <button
              key={country}
              onClick={() => handleCountryFilter(country)}
              style={{
                padding: "6px 12px",
                backgroundColor: activeCountryFilter === country ? "#64ffda" : "#1a2c44",
                color: activeCountryFilter === country ? "#0a192f" : "#e6f1ff",
                border: "1px solid #233554",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: activeCountryFilter === country ? "bold" : "normal"
              }}
            >
              {country}
            </button>
          ))}
        </div>
      )}

      {/* Komunikat o wyborze */}
      {!activeProducerFilter && !activeCountryFilter && collectionName.toLowerCase() === 'bikeframes' && (
        <div style={{
          color: "#64ffda",
          fontSize: "16px",
          textAlign: "center",
          padding: "20px",
          marginBottom: "10px"
        }}>
          Wybierz producenta, aby wyświetlić rekordy
        </div>
      )}

      {!activeProducerFilter && !activeCountryFilter && !activeUsersFilter && collectionName.toLowerCase() === 'users' && (
        <div style={{
          color: "#64ffda",
          fontSize: "16px",
          textAlign: "center",
          padding: "20px",
          marginBottom: "10px"
        }}>
          Wybierz kraj, aby wyświetlić rekordy
        </div>
      )}

      {/* Wyświetl tabelę tylko gdy wybrano filtr */}
      {(activeProducerFilter || activeCountryFilter || (collectionName.toLowerCase() === "users" && activeUsersFilter)) && (
        <>
          {isFiltering && (
            <div style={{
              marginBottom: "10px",
              color: "#64ffda",
              fontSize: "14px",
              padding: "8px",
              backgroundColor: "rgba(100, 255, 218, 0.1)",
              borderRadius: "5px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <div>
                Znaleziono <strong>{filterResults}</strong> z {records.length} rekordów
                {debouncedFilter && <span> dla frazy: "<em>{debouncedFilter}</em>"</span>}
              </div>
              <div>
                {Object.entries(debouncedColumnFilters).map(([col, val]) =>
                  val ? <span key={col} style={{
                    backgroundColor: "rgba(100, 255, 218, 0.2)",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    marginLeft: "5px",
                    fontSize: "12px"
                  }}>{col}: {val}</span> : null
                )}
              </div>
            </div>
          )}

      {debugMode && (
        <div style={{
          margin: "10px 0",
          padding: "10px",
          backgroundColor: "#112240",
          borderRadius: "5px",
          fontSize: "12px",
          color: "#e6f1ff",
          fontFamily: "monospace"
        }}>
          <div>Łączna liczba rekordów: {records.length}</div>
          <div>Po filtrowaniu i sortowaniu: {filteredData.length}</div>
          <div>Aktywny filtr: "{debouncedFilter}"</div>
          <div>Aktywne filtry kolumn: {JSON.stringify(debouncedColumnFilters)}</div>
          <div>Stan filtrowania: {isFiltering ? "AKTYWNE" : "nieaktywne"}</div>
        </div>
      )}

      {/* Informacja o postępie importu */}
      {isImporting && (
        <div style={{
          margin: "10px 0",
          backgroundColor: "#112240",
          padding: "10px",
          borderRadius: "5px"
        }}>
          <div style={{ color: "#64ffda", marginBottom: "5px" }}>
            Importowanie danych: {importProgress}%
          </div>
          <div style={{
            width: "100%",
            height: "6px",
            backgroundColor: "#1e2d45",
            borderRadius: "3px"
          }}>
            <div style={{
              width: `${importProgress}%`,
              height: "100%",
              backgroundColor: "#64ffda",
              borderRadius: "3px",
              transition: "width 0.3s"
            }}/>
          </div>
        </div>
      )}

      {/* Komunikat o błędzie, jeśli istnieje */}
      {error && (
        <div style={{
          color: "#ff3d00",
          padding: "10px",
          backgroundColor: "rgba(255, 61, 0, 0.1)",
          borderRadius: "5px",
          marginBottom: "10px",
          fontSize: "14px"
        }}>
          {error}
        </div>
      )}

      {/* Tabela danych - WAŻNE: jeśli filtrowanie aktywne, renderujemy tylko filteredData */}
      <div style={{
        overflowX: "auto",
        borderRadius: "6px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        maxWidth: "100%"
      }}>
        <table style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "auto"
        }}>
          <thead>
            <tr style={{ backgroundColor: "#112240", color: "#64ffda", fontWeight: "bold" }}>
              {visibleColumns.map(column => (
                <th
                  key={column}
                  data-column={column}
                  style={{
                    padding: "8px 6px",
                    borderBottom: "2px solid #1e2d45",
                    textAlign: "left",
                    position: "relative",
                    cursor: "pointer",
                    fontSize: "12px"
                  }}
                  onClick={() => handleSort(column)}
                  draggable
                  onDragStart={() => handleDragStart(column)}
                  onDragOver={e => handleDragOver(e, column)}
                  onDrop={handleDrop}
                >
                  {column}
                  {sortConfig?.key === column && (
                    <span style={{ marginLeft: "3px", fontSize: "10px", verticalAlign: "middle" }}>
                      {sortConfig.direction === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                  {debouncedColumnFilters[column.toLowerCase()] && (
                    <span style={{
                      marginLeft: "3px",
                      fontSize: "10px",
                      backgroundColor: "rgba(100, 255, 218, 0.3)",
                      color: "#0a192f",
                      padding: "1px 4px",
                      borderRadius: "3px"
                    }}>
                      ✓
                    </span>
                  )}
                </th>
              ))}
              <th style={{
                padding: "8px 6px",
                borderBottom: "2px solid #1e2d45",
                width: "100px",
                fontSize: "12px"
              }}>Akcje</th>
            </tr>
            {/* Wiersz filtrów kolumn */}
            <tr style={{ backgroundColor: "#0a192f" }}>
              {visibleColumns.map(column => (
                <th key={`filter-${column}`} style={{ padding: "4px" }}>
                  <input
                    type="text"
                    placeholder={`Filtruj ${column}...`}
                    value={columnFilters[column.toLowerCase()] || ""}
                    onChange={e => handleColumnFilterChange(column, e.target.value)}
                    onClick={e => e.stopPropagation()}
                    style={{
                      width: "100%",
                      padding: "4px",
                      fontSize: "11px",
                      backgroundColor: columnFilters[column.toLowerCase()] ? "rgba(100, 255, 218, 0.1)" : "#1a2c44",
                      color: "#e6f1ff",
                      border: columnFilters[column.toLowerCase()] ? "1px solid #64ffda" : "1px solid #3a506b",
                      borderRadius: "3px"
                    }}
                  />
                </th>
              ))}
              <th>
                <button
                  onClick={resetColumnFilters}
                  style={{
                    width: "100%",
                    padding: "4px",
                    backgroundColor: isFiltering ? "#ff3d00" : "#3a506b",
                    color: "white",
                    border: "none",
                    borderRadius: "3px",
                    cursor: "pointer",
                    fontSize: "11px"
                  }}
                >
                  Reset
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {(
              getSortedData((activeProducerFilter || activeCountryFilter || isFiltering) ? filteredData : records).length === 0
            ) ? (
              <tr>
                <td colSpan={visibleColumns.length + 1} style={{ textAlign: "center", padding: "15px", color: "#64ffda" }}>
                  {records.length > 0 ? "Brak wyników dla podanych filtrów" : "Brak danych do wyświetlenia"}
                </td>
              </tr>
            ) : (
              getSortedData((activeProducerFilter || activeCountryFilter || isFiltering) ? filteredData : records).map((record, idx) => {
                const keyId = record.id ? String(record.id) : `${collectionName}-fallback-${idx}`;
                return (
                  <tr
                    key={keyId}
                    onClick={() => handleRowClick(record)}
                    style={{
                      cursor: "pointer",
                      backgroundColor: "#1a2c44",
                      transition: "background-color 0.3s",
                      ...(editingRecord?.id === record.id ? { backgroundColor: "#2a3d58" } : {})
                    }}
                  >
                    {visibleColumns.map(column => {
                      // columns są lowercase (fetchRecords normalizuje) - korzystamy z tego samego klucza
                      // Bezpieczne pobranie wartości (obsługa niejednorodnych kluczy w rekordzie)
                      const rawKey = Object.keys(record).find(k => k.toLowerCase() === column) ?? column;
                      const value = formatValue(record[rawKey]);
                      const isMatch = debouncedFilter &&
                        value.toLowerCase().includes(debouncedFilter.toLowerCase());
                      const columnFilterValue = (debouncedColumnFilters[column] || debouncedColumnFilters[column.toLowerCase()] || "");
                      const isColumnMatch = columnFilterValue &&
                        value.toLowerCase().includes(columnFilterValue.toLowerCase());

                      return (
                        <td
                          key={column}
                          data-field={column}
                          style={{
                            padding: "4px 6px",
                            borderBottom: "1px solid #112240",
                            fontSize: "12px",
                            color: "#e6f1ff",
                            backgroundColor: isMatch || isColumnMatch ? "rgba(100, 255, 218, 0.1)" : undefined
                          }}
                        >
                          {(isMatch && debouncedFilter) || (isColumnMatch && columnFilterValue) ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: value.replace(
                                  new RegExp(debouncedFilter || columnFilterValue || '', 'gi'),
                                  match => `<span style="background-color: rgba(100, 255, 218, 0.4); padding: 0 2px; border-radius: 2px;">${match}</span>`
                                )
                              }}
                            />
                          ) : value}
                        </td>
                      );
                    })}
                    <td style={{
                      padding: "4px 6px",
                      borderBottom: "1px solid #112240",
                      backgroundColor: "#1e2d45",
                      textAlign: "center"
                    }}>
                      <div style={{ display: "flex", justifyContent: "center", gap: "3px" }}>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            openEditModal(e, record);
                          }}
                          style={{
                            padding: "4px 8px",
                            backgroundColor: "#64ffda",
                            border: "none",
                            borderRadius: "3px",
                            color: "#0a192f",
                            cursor: "pointer",
                            fontSize: "11px",
                            fontWeight: "bold"
                          }}
                          title="Edytuj rekord"
                        >
                          Edytuj
                        </button>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteRecord(record.id!);
                          }}
                          style={{
                            padding: "4px 8px",
                            backgroundColor: "#ff3d00",
                            border: "none",
                            borderRadius: "3px",
                            color: "#fff",
                            cursor: "pointer",
                            fontSize: "11px",
                            fontWeight: "bold"
                          }}
                          title="Usuń rekord"
                        >
                          Usuń
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
        </>
      )}

      {/* Modal edycji rekordu */}
      {editingRecord && (
        <EditModal
          record={editingRecord}
          collectionName={collectionName}
          onClose={() => setEditingRecord(null)}
          onSave={handleSaveRecord}
          columns={columns.filter(col => col !== "id")}
        />
      )}

      {/* Modal podsumowujący pominięte rekordy */}
      {showSkippedModal && (
        <div style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          zIndex: 1000
        }}>
          <h2 style={{ color: "#333", marginTop: 0 }}>Podsumowanie importu</h2>
          <p style={{ color: "#333" }}>Liczba dodanych rekordów: <strong>{records.length - skippedRecords.length}</strong></p>
          <p style={{ color: "#333" }}>Liczba pominiętych rekordów: <strong>{skippedRecords.length}</strong></p>

          {skippedRecords.length > 0 && (
            <>
              <h3 style={{ color: "#333" }}>Pominięte rekordy:</h3>
              <ul style={{ color: "#333", paddingLeft: "20px" }}>
                {skippedRecords.map((record, index) => (
                  <li key={index} style={{ marginBottom: "5px" }}>
                    {String(getFieldValueCaseInsensitive(record, "producent") ?? "")}{" "}
                    {String(getFieldValueCaseInsensitive(record, "model") ?? "")} -{" "}
                    {String(getFieldValueCaseInsensitive(record, "rok") ?? "")}{" "}
                    {String(getFieldValueCaseInsensitive(record, "rozmiar") ?? "")}
                    {record._skipReason ? ` (${record._skipReason})` : ""}
                  </li>
                ))}
              </ul>
            </>
          )}

          <button
            onClick={() => setShowSkippedModal(false)}
            style={{
              marginTop: "10px",
              padding: "10px",
              backgroundColor: "#64ffda",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              color: "#000",
              fontSize: "16px"
            }}
          >
            Zamknij
          </button>
        </div>
      )}

      {showImportPreviewModal && (
        <div style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(10, 25, 47, 0.85)",
          zIndex: 1001,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px"
        }}>
          <div style={{
            width: "min(1200px, 95vw)",
            maxHeight: "90vh",
            overflow: "auto",
            backgroundColor: "#112240",
            border: "1px solid #233554",
            borderRadius: "8px",
            padding: "16px"
          }}>
            <h2 style={{ color: "#64ffda", marginTop: 0 }}>Podgląd importu rowerów</h2>
            <div style={{ color: "#e6f1ff", marginBottom: "12px", fontSize: "13px" }}>
              Rekordy gotowe do zapisu: <strong>{recordsReadyToImport.length}</strong> / {importPreviewRows.length}
            </div>
            <div style={{ overflowX: "auto", border: "1px solid #1e2d45", borderRadius: "6px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#0a192f", color: "#64ffda" }}>
                    {importPreviewColumns.map(column => (
                      <th key={`import-preview-header-${column}`} style={{ padding: "8px", textAlign: "left" }}>
                        {column}
                      </th>
                    ))}
                    <th style={{ padding: "8px", textAlign: "left" }}>Status</th>
                    <th style={{ padding: "8px", textAlign: "left" }}>Grafika ramy</th>
                  </tr>
                </thead>
                <tbody>
                  {[...importPreviewRows]
                    .sort((a, b) =>
                      getSizeSortValue(getFieldValueCaseInsensitive(a.record, "rozmiar")) -
                      getSizeSortValue(getFieldValueCaseInsensitive(b.record, "rozmiar"))
                    )
                    .map((item, index) => (
                      <tr key={`${item.docId}-${index}`} style={{ borderBottom: "1px solid #1e2d45" }}>
                        {importPreviewColumns.map(column => (
                          <td key={`${item.docId}-${index}-${column}`} style={{ padding: "8px", color: "#e6f1ff" }}>
                            {formatValue(item.record[column])}
                          </td>
                        ))}
                        <td style={{ padding: "8px", color: item.status === "ready" ? "#64ffda" : "#ffb86c" }}>
                          {getStatusLabel(item.status)}
                          {item.reason ? `: ${item.reason}` : ""}
                        </td>
                        <td style={{ padding: "6px 8px", position: "relative" }}>
                          <div
                            onMouseEnter={() => setHoveredPreviewId(`${item.docId}-${index}`)}
                            onMouseLeave={() => setHoveredPreviewId(null)}
                            style={{ display: "inline-block" }}
                          >
                            <FrameSizePreview record={item.record} />
                            {hoveredPreviewId === `${item.docId}-${index}` && (
                              <div style={{
                                position: "absolute",
                                left: "100%",
                                top: "0",
                                marginLeft: "8px",
                                zIndex: 1002,
                                padding: "8px",
                                backgroundColor: "#0a192f",
                                border: "1px solid #64ffda",
                                borderRadius: "6px",
                                boxShadow: "0 6px 18px rgba(0,0,0,0.45)"
                              }}>
                                <FrameSizePreview record={item.record} width={320} height={180} />
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "14px" }}>
              <button
                onClick={() => {
                  setShowImportPreviewModal(false);
                  setImportPreviewRows([]);
                  setRecordsReadyToImport([]);
                  setImportBaseRecords([]);
                }}
                style={{
                  padding: "8px 12px",
                  borderRadius: "5px",
                  border: "none",
                  backgroundColor: "#3a506b",
                  color: "#e6f1ff",
                  cursor: "pointer"
                }}
              >
                Anuluj
              </button>
              <button
                onClick={performImportSave}
                disabled={recordsReadyToImport.length === 0 || isImporting}
                style={{
                  padding: "8px 12px",
                  borderRadius: "5px",
                  border: "none",
                  backgroundColor: recordsReadyToImport.length > 0 ? "#64ffda" : "#3a506b",
                  color: recordsReadyToImport.length > 0 ? "#0a192f" : "#8892b0",
                  cursor: recordsReadyToImport.length > 0 ? "pointer" : "not-allowed",
                  fontWeight: "bold"
                }}
              >
                Zapisz do DBMaria
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dodatkowa informacja debugowania filtrowania */}
      {debugMode && (
        <div style={{
          backgroundColor: "#ff9800",
          color: "black",
          padding: "8px",
          marginBottom: "10px",
          borderRadius: "5px",
          fontWeight: "bold",
          textAlign: "center",
          fontSize: "14px"
        }}>
          TRYB DEBUGOWANIA - Pokazuję {filteredData.length} z {records.length} rekordów
          {isFiltering ? " (FILTROWANIE AKTYWNE)" : " (bez filtrowania)"}
        </div>
      )}
    </div>
  );
};

export default DataTable;
